/**
 * =============================================================================
 * IDS CRM Web - Contract Variation Modal (Phase R1)
 * =============================================================================
 * Once a line item is won ("deposit paid") its pricing is LOCKED — the only
 * change path is a Contract Variation: a proposed replacement schedule with a
 * mandatory internal reason, routed to the Approvals inbox.
 *
 * Props:
 *   line        — needs opportunity_id, opp_ref, pricing_schedule, currency
 *   onClose     — close without submitting
 *   onSubmitted — called after a 201 (parent refetches + toasts)
 *
 * Input-collecting modal: the overlay does NOT close on outside click.
 * =============================================================================
 */

import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatMoney } from '../utils/money';
import './VariationModal.css';

const toMajor = (minor) => (minor || minor === 0 ? (minor / 100).toFixed(2) : '');
const toMinor = (major) => Math.round((Number(major) || 0) * 100);

function VariationModal({ line, onClose, onSubmitted }) {
  const { authFetch } = useAuth();
  const [schedule, setSchedule] = useState(
    (line.pricing_schedule || []).map(entry => ({ ...entry, amount: toMajor(entry.amount_minor_units) }))
  );
  // R5a (DL-12): non-product lines lock too — their variation proposes a
  // plain replacement value instead of a schedule.
  const isValueOnly = line.line_type === 'non_product';
  const [proposedValue, setProposedValue] = useState(toMajor(line.value_minor_units));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const nextKey = useRef(1);

  const currency = line.currency || 'GBP';
  const oneOffTotal = schedule.reduce((sum, entry) => sum + (entry.recurring ? 0 : toMinor(entry.amount)), 0);
  const monthlyTotal = schedule.reduce((sum, entry) => sum + (entry.recurring ? toMinor(entry.amount) : 0), 0);

  const updateEntry = (index, patch) => {
    setSchedule(prev => prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  };

  const addRow = () => {
    setSchedule(prev => [...prev, {
      key: `variation_${Date.now()}_${nextKey.current++}`,
      label: '',
      amount: '',
      recurring: false,
      pipeline_stage_ref: null
    }]);
  };

  const removeRow = (index) => {
    setSchedule(prev => prev.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (!reason.trim()) {
      setError('A variation reason is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = isValueOnly
        ? { reason: reason.trim(), value_minor_units: toMinor(proposedValue) }
        : {
          reason: reason.trim(),
          schedule: schedule.map(entry => ({
            key: entry.key,
            label: entry.label,
            amount_minor_units: toMinor(entry.amount),
            recurring: !!entry.recurring,
            pipeline_stage_ref: entry.pipeline_stage_ref || null
          }))
        };
      const response = await authFetch(`/api/v1/opportunities/${line.opportunity_id}/variation`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to submit the variation');
      }
      onSubmitted();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal variation-modal">
        <div className="modal-header">
          <h2>Request contract variation — {line.opp_ref}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <p className="variation-hint">
            This line item's pricing is locked (deposit paid). Propose the replacement schedule
            below — it takes effect only once approved.
          </p>
          {isValueOnly ? (
            <div className="form-group">
              <label>Proposed value ({currency})</label>
              <input
                type="number" min="0" step="0.01"
                value={proposedValue}
                onChange={(e) => setProposedValue(e.target.value)}
              />
              <span className="field-help">
                Current locked value: {formatMoney(line.value_minor_units, currency)}
              </span>
            </div>
          ) : (
          <table className="variation-schedule-table">
            <thead>
              <tr><th>Label</th><th>Amount ({currency})</th><th>Monthly</th><th></th></tr>
            </thead>
            <tbody>
              {schedule.map((entry, index) => (
                <tr key={entry.key || index}>
                  <td>
                    <input
                      type="text"
                      className="variation-label-input"
                      value={entry.label || ''}
                      onChange={(e) => updateEntry(index, { label: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="number" min="0" step="0.01"
                      value={entry.amount}
                      onChange={(e) => updateEntry(index, { amount: e.target.value })}
                    />
                  </td>
                  <td className="variation-recurring-cell">
                    <input
                      type="checkbox"
                      checked={!!entry.recurring}
                      onChange={(e) => updateEntry(index, { recurring: e.target.checked })}
                      title="Ticked = a monthly recurring amount"
                    />
                  </td>
                  <td>
                    <button type="button" className="btn-link btn-link-danger" onClick={() => removeRow(index)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="variation-totals-row">
                <td>Totals</td>
                <td colSpan={3}>
                  One-off {formatMoney(oneOffTotal, currency)}
                  {' · '}Monthly {formatMoney(monthlyTotal, currency)}
                </td>
              </tr>
            </tfoot>
          </table>
          )}
          {!isValueOnly && (
            <button type="button" className="btn-link" onClick={addRow}>+ Add entry</button>
          )}
          <div className="form-group">
            <label>Reason (internal) *</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default VariationModal;
